/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Feedback Service - API calls for feedback system
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import api from './api'

const feedbackService = {
  /**
   * Submit new feedback
   * @param {FormData} formData - Feedback data with optional screenshot
   * @returns {Promise} Created feedback object
   */
  submitFeedback: async (formData) => {
    const response = await api.post('/feedback', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data.data
  },

  /**
   * Get current user's feedback (paginated)
   * @param {Object} params - Query parameters (page, limit, status, category, sortBy, sortOrder)
   * @returns {Promise} Paginated feedback list with metadata
   */
  getMyFeedback: async (params = {}) => {
    const response = await api.get('/feedback', { params })
    return response.data.data
  },

  /**
   * Get specific feedback by ID
   * @param {string} id - Feedback ID
   * @returns {Promise} Full feedback details
   */
  getFeedbackById: async (id) => {
    const response = await api.get(`/feedback/${id}`)
    return response.data.data.feedback
  },

  // Admin-only methods

  /**
   * Get all feedback (admin only, paginated)
   * @param {Object} params - Query parameters (page, limit, status, category, sortBy, sortOrder)
   * @returns {Promise} Paginated feedback list with metadata
   */
  getAllFeedback: async (params = {}) => {
    const response = await api.get('/feedback', { params })
    return response.data.data
  },

  /**
   * Respond to feedback (admin only)
   * @param {string} id - Feedback ID
   * @param {Object} data - Response data { response, status }
   * @returns {Promise} Updated feedback object
   */
  respondToFeedback: async (id, data) => {
    const response = await api.post(`/feedback/${id}/respond`, data)
    return response.data.data.feedback
  },

  /**
   * Update feedback status (admin only)
   * @param {string} id - Feedback ID
   * @param {string} status - New status (pending, in_review, resolved, closed)
   * @returns {Promise} Updated feedback object
   */
  updateFeedbackStatus: async (id, status) => {
    const response = await api.patch(`/feedback/${id}/status`, { status })
    return response.data.data.feedback
  },

  /**
   * Delete feedback (admin only)
   * @param {string} id - Feedback ID
   * @returns {Promise} Success confirmation
   */
  deleteFeedback: async (id) => {
    const response = await api.delete(`/feedback/${id}`)
    return response.data.data
  },

  /**
   * Get feedback statistics (admin only)
   * @param {Object} params - Query parameters (fromDate, toDate)
   * @returns {Promise} Aggregated statistics
   */
  getFeedbackStats: async (params = {}) => {
    const response = await api.get('/feedback/stats', { params })
    return response.data.data
  },

  /**
   * Create GitHub issue from feedback (admin only)
   * @param {string} id - Feedback ID
   * @returns {Promise} Updated feedback with GitHub issue URL
   */
  createGitHubIssue: async (id) => {
    const response = await api.post(`/feedback/${id}/github-issue`)
    return response.data.data
  }
}

export default feedbackService
